// pages/admin/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { FiSun, FiMoon, FiUser, FiLock, FiShield } from 'react-icons/fi';
import toast from 'react-hot-toast';

function AdminLogin() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Clear any invalid storage on login page load
  useEffect(() => {
    const token = localStorage.getItem('dev_token');
    const user = localStorage.getItem('dev_user');
    
    if (token === 'undefined' || token === 'null') {
      localStorage.removeItem('dev_token');
    }
    if (user === 'undefined' || user === 'null') {
      localStorage.removeItem('dev_user');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const success = await login(formData.username, formData.password);
      
      if (success) {
        // Get user data from localStorage to check role
        const userData = JSON.parse(localStorage.getItem('dev_user') || '{}');
        
        // Check if user has admin or superadmin role
        if (userData.role === 'admin' || userData.role === 'superadmin') {
          navigate('/admin/dashboard');
        } else {
          toast.error('Access denied. Admin privileges required.');
          // Logout the user since they're not admin
          localStorage.removeItem('dev_token');
          localStorage.removeItem('dev_user');
          // Redirect to developer login
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card admin-card">
        <div className="login-header">
          <h1>🚚 Delivery7</h1>
          <button className="theme-toggle" onClick={toggleTheme} type="button">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
        </div>
        
        <div className="login-role-badge admin-badge">
          <FiShield /> Admin Portal
        </div>
        
        <h2>Admin Access</h2>
        <p className="login-subtitle">
          Secure login for administrators
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              <FiUser style={{ marginRight: '6px' }} />
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="Enter your admin username"
              required
            />
          </div>
          
          <div className="form-group">
            <label>
              <FiLock style={{ marginRight: '6px' }} />
              Password
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter your password"
              required
              minLength={6}
            />
          </div>

          <button type="submit" className="btn btn-admin" disabled={loading}>
            {loading ? 'Loading...' : '🔐 Admin Login'}
          </button>
        </form>

        <div className="login-footer">
          <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
            Need a developer account? <a href="/login" style={{ color: '#667eea' }}>Developer Login</a>
          </span>
        </div>

        <div className="login-role-badge">
          <span>🔒 Admin Access Only</span>
        </div>
      </div>

      <style>{`
        .login-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: var(--bg-primary);
          padding: 20px;
        }
        
        .login-card {
          background: var(--bg-card);
          border-radius: var(--radius);
          border: 1px solid var(--border-color);
          padding: 40px;
          max-width: 420px;
          width: 100%;
          box-shadow: var(--shadow);
        }
        
        .admin-card {
          border-top: 4px solid #ff4757;
        }
        
        .login-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .login-header h1 {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #ff4757, #ff6b81);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .login-header .theme-toggle {
          background: none;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 6px 10px;
          cursor: pointer;
          color: var(--text-primary);
          font-size: 18px;
          transition: var(--transition);
        }
        
        .login-header .theme-toggle:hover {
          background: var(--bg-hover);
        }
        
        .login-role-badge {
          text-align: center;
          margin-bottom: 20px;
        }
        
        .admin-badge {
          display: inline-block;
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          color: #ff4757;
          background: rgba(255, 71, 87, 0.15);
          border: 1px solid rgba(255, 71, 87, 0.3);
        }
        
        .login-card h2 {
          font-size: 24px;
          margin-bottom: 4px;
        }
        
        .login-subtitle {
          color: var(--text-muted);
          margin-bottom: 24px;
          font-size: 14px;
        }
        
        .login-card form .btn {
          width: 100%;
          padding: 12px;
          font-size: 16px;
          justify-content: center;
        }
        
        .login-footer {
          margin-top: 16px;
          text-align: center;
        }
        
        .login-footer a {
          text-decoration: none;
        }
        
        .login-footer a:hover {
          text-decoration: underline;
        }
        
        .login-role-badge {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color);
          text-align: center;
        }
        
        .login-role-badge span {
          display: inline-block;
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          background: var(--bg-input);
        }
        
        .form-group {
          margin-bottom: 16px;
        }
        
        .form-group label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 4px;
          color: var(--text-secondary);
        }
        
        .form-group input {
          width: 100%;
          padding: 10px 14px;
          background: var(--bg-input);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          font-size: 14px;
          transition: var(--transition);
        }
        
        .form-group input:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-light);
        }
        
        .form-group input::placeholder {
          color: var(--text-muted);
        }
        
        .btn {
          padding: 8px 20px;
          border: none;
          border-radius: var(--radius-sm);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: var(--transition);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: var(--font-family);
          line-height: 1.5;
        }
        
        .btn-admin {
          background: #ff4757;
          color: white;
        }
        
        .btn-admin:hover:not(:disabled) {
          background: #ff6b81;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
        }
        
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }
      `}</style>
    </div>
  );
}

export default AdminLogin;